import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getPublicPlans } from "../services/landingService";
import { ICONS } from "../constants/pricing";

// ── Per-card color themes (SMB-style accent per plan) ──────────────────────────
const THEMES = [
  { // Individual – violet
    border:    "border-2 border-violet-400",
    shadow:    "shadow-lg shadow-violet-100",
    iconBg:    "bg-violet-50",
    iconColor: "text-violet-500",
    check:     "text-violet-500",
    btn:       "bg-violet-500 hover:bg-violet-600 text-white",
    badgeBg:   "bg-violet-500 text-white",
    divider:   "border-violet-100",
  },
  { // Professional – indigo
    border:    "border-2 border-indigo-400",
    shadow:    "shadow-lg shadow-indigo-100",
    iconBg:    "bg-indigo-50",
    iconColor: "text-indigo-500",
    check:     "text-indigo-500",
    btn:       "bg-indigo-500 hover:bg-indigo-600 text-white",
    badgeBg:   "bg-indigo-500 text-white",
    divider:   "border-indigo-100",
  },
  { // SMB – teal (existing)
    border:    "border-2 border-teal-400",
    shadow:    "shadow-lg shadow-teal-100",
    iconBg:    "bg-teal-50",
    iconColor: "text-teal-600",
    check:     "text-teal-500",
    btn:       "bg-teal-500 hover:bg-teal-600 text-white",
    badgeBg:   "bg-teal-500 text-white",
    divider:   "border-teal-100",
  },
  { // 4th slot – cyan
    border:    "border-2 border-cyan-400",
    shadow:    "shadow-lg shadow-cyan-100",
    iconBg:    "bg-cyan-50",
    iconColor: "text-cyan-600",
    check:     "text-cyan-500",
    btn:       "bg-cyan-500 hover:bg-cyan-600 text-white",
    badgeBg:   "bg-cyan-500 text-white",
    divider:   "border-cyan-100",
  },
];

function decoratePlan(plan, index) {
  // A plan that is not yet available is listed as "Coming Soon": visible, priced,
  // but not purchasable. Interest is captured through the normal signup route.
  const comingSoon  = plan.is_active === false;
  const isHighlight = false;
  const nameLC      = (plan.name || "").toLowerCase();
  const style       = isHighlight ? "highlight" : "themed";

  const theme     = THEMES[index % THEMES.length];
  const Icon      = ICONS[index % ICONS.length];

  const rawPrice = typeof plan.price === "number" ? plan.price : parseFloat(plan.price);
  const price    = isNaN(rawPrice) ? String(plan.price) : rawPrice === 0 ? "$0" : `$${rawPrice}`;

  // "Most Popular" is retired — the badge simply names the plan.
  const badge = { text: plan.name, bg: theme.badgeBg };

  let cta;
  if (comingSoon)                         cta = "Coming Soon";
  else if (nameLC.includes("enterprise")) cta = "Talk to us";
  else                                    cta = "Start 30-Day Free Trial";

  return {
    ...plan,
    Icon, theme,
    price, period: "/mo",
    badge,
    desc: plan.description || plan.desc || "",
    features: Array.isArray(plan.features) ? plan.features : [],
    analysesLabel: nameLC.includes("enterprise")
      ? "Talk to us"
      : (plan.analyses_per_month > 0 ? String(plan.analyses_per_month) : "—"),
    seatsLabel: plan.seats > 1 ? `${plan.seats} users` : "1 user",
    comingSoon,
    cta, style,
  };
}

const getAuthPlanParam = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("individual")) return "individual";
  if (n.includes("professional")) return "professional";
  if (n.includes("business") || n.includes("smb")) return "business";
  if (n.includes("enterprise")) return "enterprise";
  return "individual";
};

// ── Component ──────────────────────────────────────────────────────────────────
const PricingSection = () => {
  const navigate = useNavigate();
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Blurb figure is read from the Individual plan row rather than written into
  // the copy — a hardcoded number here silently contradicts the enforced limit.
  const trialAnalyses = plans.find((pl) => (pl.name || "").toLowerCase().includes("individual"))
    ?.analyses_per_month ?? 5;

  useEffect(() => {
    let cancelled = false;
    getPublicPlans()
      .then((res) => {
        if (cancelled) return;
        const raw = Array.isArray(res?.data?.plans) ? res.data.plans
                  : Array.isArray(res?.data)        ? res.data
                  : Array.isArray(res)              ? res
                  : null;
        if (raw && raw.length > 0) {
          // Every plan is rendered. Inactive ones become "Coming Soon" cards rather
          // than vanishing, so the page shows the full ladder during the launch window.
          setPlans(raw.map(decoratePlan));
        }
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) { setError(err.message); setPlans([]); }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="pricing" className="bg-gradient-to-b from-white to-slate-50 py-24 px-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Pricing
          </span>
          <h2 className="text-4xl font-extrabold text-brand-text mb-4 tracking-tight">
            Simple, Transparent Pricing
          </h2>
          <p className="text-brand-muted max-w-md mx-auto text-base leading-relaxed">
            The <span className="font-semibold text-primary">Individual plan</span> includes a 30-day free trial with up to {trialAnalyses} analyses. Upgrade anytime as your needs grow.
          </p>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="flex flex-wrap justify-center gap-6">
            {[0,1,2,3].map((i) => (
              <div key={i} className="w-full sm:w-64 rounded-2xl border border-surface-border p-6 flex flex-col gap-4 animate-pulse">
                <div className="w-10 h-10 rounded-xl bg-slate-200" />
                <div className="h-8 w-20 bg-slate-200 rounded" />
                <div className="h-4 w-24 bg-slate-200 rounded" />
                <div className="flex flex-col gap-2 flex-1">
                  {[0,1,2,3].map((j) => <div key={j} className="h-3 bg-slate-100 rounded w-4/5" />)}
                </div>
                <div className="h-10 bg-slate-200 rounded-xl" />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <p className="text-center text-sm text-danger">
            Could not load pricing plans. Please try again later.
          </p>
        )}

        {/* Plan cards */}
        {!loading && !error && (
          <motion.div
            className="flex flex-wrap justify-center gap-6 items-stretch"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } } }}
          >
            {plans.map((p) => {
              const isHighlight = p.style === "highlight";
              const isEnterprise = (p.name || "").toLowerCase().includes("enterprise");
              const t = p.theme;
              return (
                <motion.div
                  key={p.name}
                  variants={{ hidden: { opacity: 0, y: 36 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } }}
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className={`relative w-full sm:w-[270px] rounded-2xl p-7 flex flex-col gap-5 bg-white ${
                    isHighlight
                      ? "bg-gradient-to-br from-primary to-indigo-600 border-0 shadow-2xl shadow-primary/30 text-white"
                      : isEnterprise
                      ? "bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 shadow-xl"
                      : `${t.border} ${t.shadow}`
                  }`}
                >
                  {/* Badge */}
                  {p.badge && (
                    <span className={`absolute -top-3.5 left-1/2 -translate-x-1/2 text-[11px] font-bold px-3.5 py-1 rounded-full whitespace-nowrap shadow ${
                      isEnterprise ? "bg-amber-400 text-slate-900" : p.badge.bg
                    }`}>
                      {p.badge.text}
                    </span>
                  )}

                  {/* Icon */}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    isHighlight ? "bg-white/20" : isEnterprise ? "bg-white/10" : t.iconBg
                  }`}>
                    <p.Icon className={`w-5 h-5 ${
                      isHighlight ? "text-white" : isEnterprise ? "text-amber-400" : t.iconColor
                    }`} />
                  </div>

                  {/* Price — hidden for enterprise */}
                  {!isEnterprise && (
                    <div className="flex items-end gap-1 leading-none">
                      <span className={`text-4xl font-extrabold tracking-tight ${isHighlight ? "text-white" : "text-brand-text"}`}>
                        {p.price}
                      </span>
                      <span className={`text-sm pb-1 ${isHighlight ? "text-white/70" : "text-brand-muted"}`}>{p.period}</span>
                    </div>
                  )}

                  {/* Name + desc */}
                  <div>
                    <p className={`font-bold text-base leading-tight ${isHighlight ? "text-white" : isEnterprise ? "text-white" : "text-brand-text"}`}>{p.name}</p>
                    {p.desc && (
                      <p className={`text-xs mt-1 leading-relaxed ${
                        isHighlight ? "text-white/70" : isEnterprise ? "text-slate-400" : "text-brand-muted"
                      }`}>{p.desc}</p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className={`border-t ${
                    isHighlight ? "border-white/20" : isEnterprise ? "border-white/10" : t.divider
                  }`} />

                  {/* The only three things that differ between plans: price, analyses, seats. */}
                  <div className={`rounded-xl px-4 py-3 flex flex-col gap-2 text-sm ${
                    isHighlight ? "bg-white/10" : isEnterprise ? "bg-white/5 border border-white/10" : "bg-slate-50 border border-surface-border"
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className={isHighlight ? "text-white/70" : isEnterprise ? "text-slate-400" : "text-brand-muted"}>Analyses / month</span>
                      <span className={`font-bold tabular-nums ${
                        isHighlight ? "text-white" : isEnterprise ? "text-white" : "text-brand-text"
                      }`}>{p.analysesLabel}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={isHighlight ? "text-white/70" : isEnterprise ? "text-slate-400" : "text-brand-muted"}>Seats</span>
                      <span className={`font-bold tabular-nums ${
                        isHighlight ? "text-white" : isEnterprise ? "text-white" : "text-brand-text"
                      }`}>{p.seatsLabel}</span>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="flex flex-col gap-2.5 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className={`flex items-start gap-2.5 text-sm ${
                        isHighlight ? "text-white/90" : isEnterprise ? "text-slate-300" : "text-brand-text"
                      }`}>
                        <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          isHighlight ? "text-white/80" : isEnterprise ? "text-amber-400" : t.check
                        }`} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    onClick={() => navigate(`/auth/register?plan=${getAuthPlanParam(p.name)}`)}
                    className={`w-full mt-2 py-3 rounded-xl font-semibold text-sm transition-all ${
                      p.comingSoon
                        ? "bg-slate-100 text-slate-600 border border-slate-300 hover:bg-slate-200"
                        : isHighlight
                        ? "bg-white text-primary hover:bg-white/90 shadow"
                        : isEnterprise
                        ? "bg-amber-400 text-slate-900 hover:bg-amber-300 flex items-center justify-center gap-2"
                        : t.btn
                    }`}
                  >
                    {p.cta}
                    {isEnterprise && !p.comingSoon && <ArrowRight className="w-4 h-4" />}
                  </button>
                  {p.comingSoon && (
                    <p className="text-center text-[11px] text-brand-muted -mt-1">
                      Register your interest — we'll email you at launch.
                    </p>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default PricingSection;
